// @ts-check
import { supabase } from '@/lib/supabase';

/**
 * @typedef {Object} SafeWriteToastEvent
 * @property {'offline' | 'online'} kind
 */

/**
 * @typedef {(event: SafeWriteToastEvent) => void} SafeWriteToastDispatch
 */

// Module-level toast dispatcher. ToastProvider calls
// registerSafeWriteToast() on mount to wire itself in; if no provider
// is mounted (tests, vanilla pages), dispatches are silent no-ops.
// Lives here so safeWrite can stay a plain async function — no React
// hooks needed at the call site.

/** @type {SafeWriteToastDispatch | null} */
let toastDispatch = null;
let lastFailureAt = 0;
let prevWriteFailed = false;

/**
 * Register a toast dispatcher for offline / online transitions.
 * Pass a function on mount; pass null on unmount to clear.
 *
 * @param {SafeWriteToastDispatch | null} fn
 */
export function registerSafeWriteToast(fn) {
  toastDispatch = typeof fn === 'function' ? fn : null;
}

// Race a promise against a timer. If the promise does not settle within
// `ms` milliseconds, resolves to `fallback` instead. Rejected promises
// also resolve to `fallback`. Never throws. Same pattern as
// main.js:withTimeout so a stalled Supabase upsert cannot freeze React
// onboarding flows (ConsentMessage, PathAssessment, BreathingDiscovery,
// FoundationLesson, ProgramReveal, SessionScreen) on a button click.

/**
 * @template T
 * @param {Promise<T> | T} promise
 * @param {number} ms
 * @param {T | null} fallback
 * @returns {Promise<T | null>}
 */
async function withTimeout(promise, ms, fallback) {
  const sentinel = Symbol('safeWrite_timeout');
  let timer;
  try {
    const result = await Promise.race([
      Promise.resolve(promise).catch(() => sentinel),
      new Promise((resolve) => { timer = setTimeout(() => resolve(sentinel), ms); }),
    ]);
    return result === sentinel ? fallback : result;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Safe write to Supabase user_progress table.
 * Upserts the data blob and saves to localStorage as fallback.
 *
 * @param {string} userId - Supabase user ID
 * @param {Object} data - The full user data object to persist
 * @returns {Promise<boolean>} true if Supabase write succeeded within 3s
 */
export async function safeWrite(userId, data) {
  // Always save to localStorage as fallback — runs unconditionally so the
  // caller's state is persisted even if Supabase is unreachable or slow.
  try {
    localStorage.setItem('bb7', JSON.stringify(data));
  } catch { /* localStorage quota or disabled */ }

  if (!userId || !supabase) return false;

  try {
    // 3s race so a stalled upsert can't hang React onboarding flows that
    // `await safeWrite()` inside a button click handler. On timeout we
    // return false (treated as "Supabase write did not confirm"); the
    // caller has already persisted to localStorage and can proceed.
    const result = await withTimeout(
      supabase
        .from('user_progress')
        .upsert(
          { id: userId, data, updated_at: new Date().toISOString() },
          { onConflict: 'id' }
        ),
      3000,
      null
    );
    if (!result) {
      console.warn('safeWrite: Supabase upsert slow or unavailable, localStorage is the source of truth');
      notifyFailure();
      return false;
    }
    if (result.error) throw result.error;
    notifyRecovery();
    return true;
  } catch (err) {
    console.error('Supabase write failed:', err);
    notifyFailure();
    return false;
  }
}

// Publish failure / recovery once per state transition so a flurry of
// safeWrite calls during an offline blip produces one toast, not five.
// 30s minimum between failure toasts to avoid spamming the user when
// Supabase is genuinely down.
function notifyFailure() {
  const now = Date.now();
  if (!prevWriteFailed || now - lastFailureAt > 30_000) {
    prevWriteFailed = true;
    lastFailureAt = now;
    toastDispatch?.({ kind: 'offline' });
  }
}
function notifyRecovery() {
  if (prevWriteFailed) {
    prevWriteFailed = false;
    toastDispatch?.({ kind: 'online' });
  }
}
